import React, { useState, useEffect } from "react";
import MenuCard from "./MenuCard";
import "../../css/MenuManagement.css";
import { axiosPostApi } from "../../helpers/interceptorServices";
import { apiList } from "../../helpers/apiList";
import CustomAlert from "../../helpers/CustomAlert";

const MenuManagement = () => {
  const loggedInUserName = sessionStorage.getItem("username");
  const [activeController, setActiveController] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("");
  const [userName, setUserName] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [userMenuData, setUserMenuData] = useState("");
  const [noData, setNoData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isLoadingUserName, setIsLoadingUserName] = useState(false);
  const [errorMessageName, setErrorMessageName] = useState("");
  const [booleanArray, setBooleanArray] = useState([]);
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (selectedLevel === "User" && selectedProfile) {
      handleUserID();
    } else if (selectedLevel === "Role") {
      handleRole();
    }
  }, [selectedLevel, selectedProfile]); // Trigger on changes to selectedLevel or selectedProfile

  const toggleController = (controller) => {
    setActiveController(activeController === controller ? null : controller);
  };

  const fetchMenuManagementData = async () => {
    if (selectedLevel === "User") {
      try {
        const requestBody = {
          username: loggedInUserName,
          userId: selectedUser,
          userProfile: selectedProfile,
        };

        const response = await axiosPostApi(
          apiList.FETCH_USER_MENU,
          requestBody,
        );
        if (
          response?.data?.status === true &&
          response?.data?.respCode === "00"
        ) {
          const data = response?.data?.data?.userMenu;
          setUserMenuData(data);
          const booleanArray = data.split("").map((char) => char === "1");
          setBooleanArray(booleanArray);
          if (data?.length === 0) {
            setNoData(true);
          } else {
            setNoData(false);
          }
        } else {
          setNoData(true);
          setUserMenuData([]);
          if (
            response?.data?.respCode !== "01" &&
            response?.data?.data !== null
          ) {
            CustomAlert({
              title: "Failed to Fetch Data",
              text:
                response?.data?.message ||
                "No data available for the specified criteria.",
              icon: "error",
              confirmButtonText: "Retry",
            });
          }
        }
      } catch (error) {
        CustomAlert({
          title: "Error",
          text: "An error occurred while fetching the data. Please try again later.",
          icon: "error",
          confirmButtonText: "Retry",
        });
      } finally {
        setLoading(false);
      }
    } else {
      try {
        const requestBody = {
          username: loggedInUserName,
          userId: "",
          userProfile: selectedProfile,
          userRole: selectedUser,
        };

        const response = await axiosPostApi(
          apiList.MENU_FETCH_ROLE_MENU,
          requestBody,
        );
        if (
          response?.data?.status === true &&
          response?.data?.respCode === "00"
        ) {
          const data = response?.data?.data?.menuList;
          setUserMenuData(data);
          const booleanArray = data.split("").map((char) => char === "1");
          setBooleanArray(booleanArray);
          if (data?.length === 0) {
            setNoData(true);
          } else {
            setNoData(false);
          }
        } else {
          setNoData(true);
          setUserMenuData([]);
          if (
            response?.data?.respCode !== "01" &&
            response?.data?.data !== null
          ) {
            CustomAlert({
              title: "Failed to Fetch Data",
              text:
                response?.data?.message ||
                "No data available for the specified criteria.",
              icon: "error",
              confirmButtonText: "Retry",
            });
          }
        }
      } catch (error) {
        CustomAlert({
          title: "Error",
          text: "An error occurred while fetching the data. Please try again later.",
          icon: "error",
          confirmButtonText: "Retry",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleProfileChange = (event) => {
    const value = event.target.value;
    setSelectedProfile(value);
    setUserName([]);
    setSelectedUser("");
  };

  const handleLevelChange = (event) => {
    const value = event.target.value;
    setSelectedLevel(value);
    setUserName([]);
    setSelectedUser("");
  };

  const handleUserID = () => {
    if (selectedLevel !== "User" || !selectedProfile) {
      return; // Ensure correct level and profile
    }
    const requestBody = {
      username: loggedInUserName,
      userId: "",
      userProfile: selectedProfile,
      status: "1",
    };

    axiosPostApi(
      `${apiList.FETCH_USER_DATA}?pageSize=${pageSize}&pageNo=${currentPage}`,
      requestBody,
    )
      .then((response) => {
        if (response?.status === 200 && response?.data?.data) {
          setUserName(response?.data?.data);
          setIsLoadingUserName(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
    // }
  };

  const handleRole = () => {
    if (!selectedProfile) return;
    setIsLoadingUserName(true);
    const requestBody = {
      username: loggedInUserName,
      userProfile: selectedProfile,
    };

    axiosPostApi(apiList.MENU_USERROLE_DATA, requestBody)
      .then((response) => {
        if (
          response?.status === 200 &&
          response?.data?.data?.userRolesWithNames
        ) {
          const roles = response.data.data.userRolesWithNames.map((role) => ({
            userId: role.userRole,
            name: role.roleName,
          }));
          setUserName(roles); // Set roles as user list
          setIsLoadingUserName(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setIsLoadingUserName(false);
      });
    // }
  };

  const selectedUserDetails =
    selectedLevel === "User" && selectedUser
      ? userName.find((user) => String(user.userId) === String(selectedUser))
      : null;

  const handleDropdownChange = (event) => {
    setSelectedUser(event.target.value);
  };

  const handleSearchMenu = () => {
    if (selectedProfile && (selectedUser || selectedLevel)) {
      fetchMenuManagementData();
    }
  };

  const handleToggle = (baseIndex, toggleIndex) => {
    // Combine the baseIndex with the toggleIndex to get the actual index in booleanArray
    const actualIndex = baseIndex + toggleIndex;
    // Create a copy of the booleanArray
    const updatedArray = [...booleanArray];

    // Toggle the value at the correct index
    updatedArray[actualIndex] = !updatedArray[actualIndex];

    // Update the state with the new booleanArray
    setBooleanArray(updatedArray);
  };

  const modifyMenuManagementData = async () => {
    let updatedMenuArray = userMenuData.split("");
    booleanArray.forEach((currentValue, index) => {
      const originalValue = userMenuData[index]; // '0' or '1' from the original data

      // If there's a mismatch, modify the value in the array
      if (
        (currentValue && originalValue === "0") ||
        (!currentValue && originalValue === "1")
      ) {
        updatedMenuArray[index] = currentValue ? "1" : "0"; // Update the value at the specific index
      }
    });

    const updatedMenuString = updatedMenuArray.join("");
    try {
      const requestBody = {
        username: loggedInUserName,
        userId: selectedLevel === "User" ? selectedUser : "",
        userProfile: selectedProfile,
        userMenu: updatedMenuString,
        userRole: selectedLevel === "Role" ? selectedUser : "",
      };

      const response = await axiosPostApi(
        apiList.UPDATE_USER_MENU,
        requestBody,
      );

      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        CustomAlert({
          title: "Success",
          text: "Menu updated successfully!",
          icon: "success",
          confirmButtonText: "Okay",
        });
        // Reset state after successful API call
        setSelectedProfile(""); // Reset profile selection
        setSelectedLevel(""); // Reset level selection
        setSelectedUser(""); // Reset user/role selection
        setUserMenuData(null); // Clear menu data
        setBooleanArray(new Array(booleanArray.length).fill(false)); // Reset toggles to all 'false'
        setActiveController(""); // Reset active controller, if applicable
        setErrorMessageName(""); // Clear any error message
        setUserName([]); // Clear user dropdown options
      } else {
        CustomAlert({
          title: "Failed to Update Data",
          text:
            response?.data?.message ||
            "An error occurred while updating the menu.",
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    } catch (error) {
      CustomAlert({
        title: "Error",
        text: "An error occurred while updating the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    }
  };

  return (
    <div className="menu-management">
      <section className="edit-user-section">
        <h2 className="menu-management-section-header">Edit User Permission</h2>
        <div className="edit-user-permission-container">
          <div className="user-dropdown-division">
            <label htmlFor="userProfileDropdown">User Profile</label>
            <select
              className="menu-select"
              id="userProfileDropdown"
              value={selectedProfile}
              onChange={handleProfileChange}
            >
              <option value="" disabled>
                Select Profile
              </option>
              <option value="CUSTOMER">CUSTOMER</option>
              {/* <option value="VENDOR">VENDOR</option>
        <option value="RETAILERS">RETAILERS</option> */}
              <option value="OPERATION">OPERATION</option>
            </select>
          </div>

          <div className="user-dropdown-division">
            <label htmlFor="userLevelDropdown">User Level</label>
            <select
              className="menu-select"
              id="userLevelDropdown"
              value={selectedLevel}
              onChange={handleLevelChange}
              style={{ cursor: !selectedProfile ? "not-allowed" : "pointer" }}
              disabled={!selectedProfile}
            >
              <option value="" disabled>
                Select Level
              </option>
              <option value="Role">Role</option>
              <option value="User">User</option>
            </select>
          </div>

          <div className="user-dropdown-division">
            <label htmlFor="userNameDropdown">Name List</label>
            <div className="dropdown-with-error">
              <select
                className="menu-select"
                id="userNameDropdown"
                style={{ cursor: !selectedLevel ? "not-allowed" : "pointer" }}
                onChange={handleDropdownChange}
                value={selectedUser}
                disabled={!selectedLevel}
              >
                <option value="" disabled>
                  {isLoadingUserName ? "Loading..." : "Select User/Role"}
                </option>
                {!isLoadingUserName && userName.length > 0
                  ? userName.map((user) => (
                    <option key={user.userId} value={user.userId}>
                      {user.name}
                    </option>
                  ))
                  : !isLoadingUserName && (
                    <option value="" disabled>
                      No data found
                    </option>
                  )}
              </select>
              {errorMessageName && (
                <div className="error-message">{errorMessageName}</div>
              )}
            </div>
          </div>

          <button
            className="menu-user-profile-search-btn"
            disabled={!selectedProfile || !selectedUser}
            style={{
              cursor:
                !selectedProfile || !selectedUser ? "not-allowed" : "pointer",
            }}
            onClick={handleSearchMenu}
          >
            Search
          </button>
        </div>
      </section>

      {selectedUserDetails && (
        <section className="user-details-section">
          <h2 className="menu-management-section-header">User Details</h2>
          <div className="user-details-container">
            {[
              ["Company ID", selectedUserDetails.userCode],
              ["Company Name", selectedUserDetails.customerName],
              ["User ID", selectedUserDetails.userId],
              ["User Role", selectedUserDetails.roleName],
            ].map(([label, value]) => (
              <div className="user-details-item" key={label}>
                <span className="user-details-label">{label}</span>
                <span className="user-details-value">{value || "-"}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="manage-team-members-section">
        <h2 className="menu-management-section-header">
          Manage Your Team members and their account permissions here
        </h2>
        {!userMenuData ? (
          <p
            className="text-start fw-bold"
            style={{
              marginTop: "7rem",
              marginBottom: "8rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            No items found
          </p>
        ) : (
          <>
            <div className="menu-card-container">
              {/* <MenuCard
                title="User Controller"
                isActive={activeController === "User Controller"}
                onToggle={() => toggleController("User Controller")}
                options={["Fetch", "Create", "Modify", "Delete", "Authorize"]}
                toggleStatus={booleanArray.slice(1, 6)} // Skip index 0, start at 1
                onToggleChange={(index) => handleToggle(1, index)} // Adjust for base index 1
              /> */}
              {/* <MenuCard
                title="Vendor Controller"
                isActive={activeController === "Vendor Controller"}
                onToggle={() => toggleController("Vendor Controller")}
                options={["Fetch", "Create", "Modify", "Delete", "Authorize"]}
                toggleStatus={booleanArray.slice(6, 11)} // Start from index 6 for Vendor
                onToggleChange={(index) => handleToggle(6, index)} // Adjust for base index 6
              /> */}
              {/* <MenuCard
                title="CompanyController"
                isActive={activeController === "CompanyController"}
                onToggle={() => toggleController("CompanyController")}
                options={[
                  "Fetch",
                  "Create",
                  "Modify",
                  "Delete",
                  "Authorize",
                  "DLT",
                ]}
                toggleStatus={booleanArray.slice(11, 17)} // Start from index 11 for Customer
                onToggleChange={(index) => handleToggle(11, index)} // Adjust for base index 11
              /> */}
              <MenuCard
                title="User Master"
                isActive={activeController === "User Master"}
                onToggle={() => toggleController("User Master")}
                options={[
                  "Fetch",
                  "Create",
                  "Modify",
                  "Delete",
                  "Authorize",
                  "Audit Trial",
                  "Send Credentials",
                ]}
                toggleStatus={[
                  ...booleanArray.slice(1, 6),
                  booleanArray[54],
                  booleanArray[82],
                ]}
                onToggleChange={(index) =>
                  handleToggle(
                    index === 5 ? 54 : index === 6 ? 82 : 1 + index,
                    0,
                  )
                }
              />

              <MenuCard
                title="Vendor Master"
                isActive={activeController === "Vendor Master"}
                onToggle={() => toggleController("Vendor Master")}
                options={[
                  "Fetch",
                  "Create",
                  "Modify",
                  "Delete",
                  "Authorize",
                  "Audit Trail",
                ]}
                toggleStatus={[...booleanArray.slice(6, 11), booleanArray[55]]}
                onToggleChange={(index) =>
                  handleToggle(index === 5 ? 55 : 6 + index, 0)
                }
              />

              <MenuCard
                title="Company Master"
                isActive={activeController === "Company Master"}
                onToggle={() => toggleController("Company Master")}
                options={[
                  "Fetch",
                  "Create",
                  "Modify",
                  "Delete",
                  "Authorize",
                  "DLT",
                  "Audit Trail",
                ]}
                toggleStatus={[...booleanArray.slice(11, 17), booleanArray[56]]}
                onToggleChange={(index) =>
                  handleToggle(index === 6 ? 56 : 11 + index, 0)
                }
              />
              {/* <MenuCard
                title="Retailer Controller"
                isActive={activeController === "Retailer Controller"}
                onToggle={() => toggleController("Retailer Controller")}
                options={["Fetch", "Create", "Modify", "Delete", "Authorize"]}
                toggleStatus={booleanArray.slice(17, 22)} // Start from index 16 for Retailer
                onToggleChange={(index) => handleToggle(17, index)} // Adjust for base index 16
              /> */}
              <MenuCard
                title="Transaction Controller"
                isActive={activeController === "Transaction Controller"}
                onToggle={() => toggleController("Transaction Controller")}
                options={[
                  "Purchase Credit details",
                  "Purchase Credits",
                  // "Top-up SMS details",
                  // "Top-up SMS",
                  "SMS API Document",
                  "Customer SMS Logs",
                  "Customer RCS Logs",
                  "RCS API Document",
                ]}
                // toggleStatus={booleanArray.slice(22, 28)} // Start from index 21 for Transaction
                toggleStatus={booleanArray.slice(22, 28)} // Start from index 21 for Transaction
                onToggleChange={(index) => handleToggle(22, index)} // Adjust for base index 21
              />
              <MenuCard
                title="Others"
                isActive={activeController === "Others"}
                onToggle={() => toggleController("Others")}
                options={["Menu Management", "Settings", "System SMS Logs"]}
                toggleStatus={booleanArray.slice(28, 31)}
                onToggleChange={(index) => handleToggle(28, index)}
              />
              <MenuCard
                title="Role Controller"
                isActive={activeController === "Role Controller"}
                onToggle={() => toggleController("Role Controller")}
                options={["Fetch", "Create", "Modify", "Delete", "Authorize"]}
                toggleStatus={booleanArray.slice(31, 36)}
                onToggleChange={(index) => handleToggle(31, index)}
              />
              {/* <MenuCard
                title="Top Up Request Controller"
                isActive={activeController === "Top Up Request Controller"}
                onToggle={() => toggleController("Top Up Request Controller")}
                options={["Fetch", "Create", "Checker", "Delete", "Authorize"]}
                toggleStatus={booleanArray.slice(36, 41)}
                onToggleChange={(index) => handleToggle(36, index)}
              /> */}
              <MenuCard
                title="Vendor transaction History"
                isActive={activeController === "Vendor transaction History"}
                onToggle={() => toggleController("Vendor transaction History")}
                options={["Fetch"]}
                toggleStatus={booleanArray.slice(41, 42)}
                onToggleChange={(index) => handleToggle(41, index)}
              />
              <MenuCard
                title="Bulk SMS"
                isActive={activeController === "Bulk SMS"}
                onToggle={() => toggleController("Bulk SMS")}
                options={["Fetch", "Create"]}
                toggleStatus={booleanArray.slice(42, 44)}
                onToggleChange={(index) => handleToggle(42, index)}
              />

              {/* <MenuCard
                title="Top-Up SMS/RCS"
                isActive={activeController === "Top-Up SMS/RCS"}
                onToggle={() => toggleController("Top-Up SMS/RCS")}
                options={["Fetch", "Create", "Checker", "Delete", "Authorize"]}
                toggleStatus={booleanArray.slice(44, 49)}
                onToggleChange={(index) => handleToggle(44, index)}
              /> */}

              <MenuCard
                title="Top-Up SMS/RCS"
                isActive={activeController === "Top-Up SMS/RCS"}
                onToggle={() => toggleController("Top-Up SMS/RCS")}
                options={[
                  "Fetch",
                  "Create",
                  "Checker",
                  "Delete",
                  "Authorize",
                  "Top-Up-SMS",
                  "Top-Up-RCS",
                ]}
                // toggleStatus={[
                //   ...booleanArray.slice(44, 49),
                //   booleanArray[85],
                //   booleanArray[86],
                // ]}
                // onToggleChange={(index) => {
                //   if (index < 5) {
                //     handleToggle(44, index); // First 5 toggles (Fetch → Authorize)
                //   } else if (index === 5) {
                //     handleToggle(85, 0); // Top-Up-SMS
                //   } else {
                //     handleToggle(86, 0); // Top-Up-RCS
                //   }
                // }}
                toggleStatus={[...booleanArray.slice(44, 49), ...booleanArray.slice(84, 86)]}
                onToggleChange={(index) => {
                  if (index < 5) {
                    handleToggle(44, index); // First 5 toggles (Fetch → Authorize)
                  } else {
                    // 5 = Top-Up-SMS, 6 = Top-Up-RCS
                    handleToggle(index === 5 ? 84 : 85, 0);
                  }
                }}
              />

              <MenuCard
                title="Main Wallet"
                isActive={activeController === "Main Wallet"}
                onToggle={() => toggleController("Main Wallet")}
                options={[
                  "Fetch",
                  "Create",
                  "Modify",
                  "Delete",
                  "Authorize",
                  "Audit Trail",
                  "DLT",
                  "Checker",
                ]}
                // toggleStatus={booleanArray.slice(49, 54)}
                toggleStatus={[
                  booleanArray[49], // Fetch
                  booleanArray[50], // Create
                  booleanArray[57], // Modify (custom index)
                  booleanArray[53], // Delete
                  booleanArray[51], // Authorize
                  booleanArray[52], // Audit Trail
                  booleanArray[58], // DLT
                  booleanArray[59], // Checker
                ]}
                onToggleChange={(index) => {
                  const mapping = [49, 50, 57, 53, 51, 52, 58, 59];
                  handleToggle(mapping[index], 0);
                }}
              // onToggleChange={(index) => handleToggle(49, index)}
              />

              <MenuCard
                title="Recharge"
                isActive={activeController === "Recharge"}
                onToggle={() => toggleController("Recharge")}
                options={["Fetch"]}
                toggleStatus={booleanArray.slice(60, 61)}
                onToggleChange={(index) => handleToggle(60, index)}
              />

              <MenuCard
                title="Retailer (Courier)"
                isActive={activeController === "Retailer (Courier)"}
                onToggle={() => toggleController("Retailer (Courier)")}
                options={[
                  "Fetch",
                  "Create",
                  "Modify",
                  "Delete",
                  "Authorize",
                  "Checker",
                ]}
                toggleStatus={[...booleanArray.slice(61, 66), booleanArray[75]]}
                onToggleChange={(index) =>
                  handleToggle(index === 5 ? 75 : 61 + index, 0)
                }
              />
              <MenuCard
                title="Order Master"
                isActive={activeController === "Order Master"}
                onToggle={() => toggleController("Order Master")}
                options={["Fetch"]}
                toggleStatus={booleanArray.slice(66, 67)}
                onToggleChange={(index) => handleToggle(66, index)}
              />

              <MenuCard
                title="Ledger"
                isActive={activeController === "Ledger"}
                onToggle={() => toggleController("Ledger")}
                options={["Fetch"]}
                toggleStatus={booleanArray.slice(67, 68)}
                onToggleChange={(index) => handleToggle(67, index)}
              />

              <MenuCard
                title="RCS Master"
                isActive={activeController === "RCS Standalone Msg"}
                onToggle={() => toggleController("RCS Standalone Msg")}
                options={[
                  "RCS Standalone Msg",
                  "RCS Plain Text",
                  "RCS Carousel",
                ]}
                toggleStatus={booleanArray.slice(68, 71)}
                onToggleChange={(index) => handleToggle(68 + index, 0)}
              />

              <MenuCard
                title="WhatsApp Features"
                isActive={activeController === "WhatsApp Features"}
                onToggle={() => toggleController("WhatsApp Features")}
                options={["WhatsApp Template"  /* ,"WhatsApp Campaign" */]}
                toggleStatus={[booleanArray[83] /* , booleanArray[84] */]}
                onToggleChange={(index) => {
                  if (index === 0) {
                    handleToggle(83, 0); // WhatsApp Template
                  } /* else {
                    handleToggle(84, 0); // WhatsApp Campaign
                  } */
                }}
              />

              <MenuCard
                title="Customer Enquiries"
                isActive={activeController === "Customer Enquiries"}
                onToggle={() => toggleController("Customer Enquiries")}
                options={["Fetch"]}
                toggleStatus={booleanArray.slice(75, 76)}
                onToggleChange={(index) => handleToggle(75, index)}
              />
              <MenuCard
                title="Pickup Address"
                isActive={activeController === "Pickup Address"}
                onToggle={() => toggleController("Pickup Address")}
                options={["Fetch", "Create", "Modify"]}
                toggleStatus={booleanArray.slice(77, 81)}
                onToggleChange={(index) => handleToggle(77, index)}
              />

              <MenuCard
                title="AI Dashboard"
                isActive={activeController === "AI Dashboard"}
                onToggle={() => toggleController("AI Dashboard")}
                options={["Fetch"]}
                toggleStatus={booleanArray.slice(87, 88)}
                onToggleChange={(index) => handleToggle(87, index)}
              />

            </div>
            <div className="submit-container">
              <button className="submit-btn" onClick={modifyMenuManagementData}>
                Submit
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default MenuManagement;
//Last Used 54-86
// WhatsApp Template - Index 83
// WhatsApp Campaign - Index 84
// Top-Up-SMS - Index 85
// Top-Up-RCS - Index 86
// In MasterTable.jsx, RCS Agent Details have array number 86
